import React from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Dropdown, Checkbox } from 'react-toolbox';
import TextArea from 'components/TextArea';
import S from '../../TemplateStyling.scss';
import parseMeasurement from './measurementParsing';

const windowConfigTypes = [
  { value: 'Single Hung', label: 'Single Hung' },
  { value: 'Double Hung', label: 'Double Hung' },
  { value: 'Sliding', label: 'Sliding' },
  { value: 'Casement', label: 'Casement' },
  { value: 'Awning', label: 'Awning' },
  { value: 'Special Shapes', label: 'Special Shapes' },
  { value: 'Picture', label: 'Picture' },
  { value: 'Transom', label: 'Transom' },
  { value: 'Combination', label: 'Combination' },
];

const windowFrameTypes = [
  { value: 'Block', label: 'Block Frame' },
  { value: 'Nailing Fin', label: 'Nailing Fin Frame' },
  { value: 'Nailing Fin w/J-Channel', label: 'Nailing Fin w/J-Channel Frame' },
  { value: 'Flush Flange', label: 'Flush Flange Frame' },
];

function WindowMeasures({ measures, onChange }) {
  const handleFieldChange = (idx, field, value) => {
    const updated = measures.map((m, i) =>
      (i === idx ? { ...m, [field]: value } : m),
    );
    onChange(updated);
  };

  const handleNestedChange = (idx, group, subfield, value) => {
    const updated = measures.map((m, i) => {
      if (i === idx) {
        return { ...m, [group]: { ...m[group], [subfield]: value } };
      }
      return m;
    });
    onChange(updated);
  };

  const handleCheckboxChange = (idx, field, value) => {
    handleFieldChange(idx, field, value);
  };

  const addRow = () => {
    onChange([
      ...measures,
      {
        openingNumber: '',
        location: '',
        qty: '',
        configuration: '',
        frameType: '',
        frameDepth: '',
        unitSize: { width: '', height: '' },
        roughOpening: { width: '', height: '' },
        temp: false,
        obsc: false,
        trans: false,
        notes: '',
      },
    ]);
  };

  const removeRow = (idx) => {
    onChange(measures.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <h6>Windows Measured</h6>
      {measures.map((measure, idx) => (
        <div key={measure.openingNumber || `window-${idx}`} style={{ border: '1px solid #eee', marginBottom: 10, padding: 10 }}>
          <div className={S.container}>
            <strong>ALL MEASURES MUST BE IN INCHES</strong>
            <strong>Opening #{idx + 1}</strong>
          </div>
          <Input label="Location" value={measure.location} onChange={v => handleFieldChange(idx, 'location', v)} />
          <Input label="Qty" type="number" value={measure.qty} onChange={v => handleFieldChange(idx, 'qty', v)} />
          <Dropdown label="Configuration" source={windowConfigTypes} value={measure.configuration} onChange={v => handleFieldChange(idx, 'configuration', v)} />
          <Dropdown label="Frame Type" source={windowFrameTypes} value={measure.frameType} onChange={v => handleFieldChange(idx, 'frameType', v)} />
          <Input label="Frame Depth" hint="e.g. 34 1/2" value={measure.frameDepth} onChange={v => handleFieldChange(idx, 'frameDepth', v)} />
          <strong>UNIT SIZE</strong>
          <Input label="Width" hint="e.g. 34 1/2" value={measure.unitSize.width} onChange={v => handleNestedChange(idx, 'unitSize', 'width', v)} />
          <Input label="Height" hint="e.g. 34 1/2" value={measure.unitSize.height} onChange={v => handleNestedChange(idx, 'unitSize', 'height', v)} />
          <strong>ROUGH OPENING</strong>
          <Input label="Width" hint="e.g. 34 1/2" value={measure.roughOpening.width} onChange={v => handleNestedChange(idx, 'roughOpening', 'width', v)} />
          <Input label="Height" hint="e.g. 34 1/2" value={measure.roughOpening.height} onChange={v => handleNestedChange(idx, 'roughOpening', 'height', v)} />
          <strong>TEMP/OBSC/TRANS</strong>
          <Checkbox className={S.tempObscTransCheckbox} label="Temp" checked={measure.temp} onChange={v => handleCheckboxChange(idx, 'temp', v)} />
          <Checkbox className={S.tempObscTransCheckbox} label="Obsc" checked={measure.obsc} onChange={v => handleCheckboxChange(idx, 'obsc', v)} />
          <Checkbox className={S.tempObscTransCheckbox} label="Trans" checked={measure.trans} onChange={v => handleCheckboxChange(idx, 'trans', v)} />
          <TextArea
            label="Notes"
            value={measure.notes || ''}
            onChange={e => handleFieldChange(idx, 'notes', e.target.value)}
          />
          <Button className={S.removeButton} label="Remove" onClick={() => removeRow(idx)} />
        </div>
      ))}
      <Button className={S.button} label="Add Window" onClick={addRow} />
    </div>
  );
}

WindowMeasures.propTypes = {
  measures: PropTypes.arrayOf(
    PropTypes.shape({
      openingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      location: PropTypes.string,
      qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      configuration: PropTypes.string,
      frameType: PropTypes.string,
      frameDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unitSize: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      roughOpening: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      temp: PropTypes.bool,
      obsc: PropTypes.bool,
      trans: PropTypes.bool,
      notes: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default WindowMeasures;

export function validateWindowMeasures(measures) {
  const errors = {};
  measures.forEach((item, idx) => {
    const { qty } = item;
    if (qty !== undefined && qty !== '' && isNaN(Number(qty))) {
      if (!errors[idx]) errors[idx] = {};
      errors[idx].qty = ['qty must be a number.'];
    }
    const measurementFields = [
      'frameDepth',
    ];
    measurementFields.forEach((field) => {
      const value = item[field];
      if (value !== undefined && value !== '' && isNaN(parseMeasurement(value))) {
        if (!errors[idx]) errors[idx] = {};
        errors[idx][field] = [`${field} must be a number or fraction.`];
      }
    });
    if (item.unitSize) {
      ['width', 'height'].forEach((subfield) => {
        const value = item.unitSize[subfield];
        if (value !== undefined && value !== '' && isNaN(parseMeasurement(value))) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`unitSize.${subfield}`] = [`unitSize.${subfield} must be a number or fraction.`];
        }
      });
    }
    if (item.roughOpening) {
      ['width', 'height'].forEach((subfield) => {
        const value = item.roughOpening[subfield];
        if (value !== undefined && value !== '' && isNaN(parseMeasurement(value))) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`roughOpening.${subfield}`] = [`roughOpening.${subfield} must be a number or fraction.`];
        }
      });
    }
  });
  if (Object.keys(errors).length > 0) {
    errors.windowMeasures = [
      'Please ensure all numeric fields are valid numbers.',
    ];
  }
  return errors;
}



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/ifiTemplate/templates/components/measures/WindowMeasures.jsx